import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TablePaginationProps,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import SearchIcon from "@mui/icons-material/Search";

import { Callout, PageHeader, useToast } from "../../components/ui";
import type { CSITipaltiStaging } from "../../types/CSITypes";
import { isAdmin } from "../../utils/auth";
import agent from "../../utils/axios";
import {
  BillDetailModal,
  PayeeDetailModal,
  PayeeModal,
  TinMatchModal,
} from "./Modals";
import { REVIEW_FILTER_OPTIONS, REVIEW_RESULTS } from "./reviewResults";
import {
  ALL_ROWS,
  buildReviewPayload,
  effectivePageSize,
  ROWS_PER_PAGE_OPTIONS,
  toStagingRow,
  TriState,
} from "./stagingData";
import StagingRow, { STAGING_COLUMN_COUNT } from "./StagingRow";
import { TriFilterChip } from "./TriFilterChip";
import { useCurrentUserName } from "./useCurrentUserName";

type GroupsMode = "all" | "single" | "multiple";

/** The bulk memo must be at least this long (as before). */
const MEMO_MIN_LENGTH = 7;

const plural = (n: number, word: string) =>
  `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;

const readItems = (response: any): unknown[] =>
  Array.isArray(response?.data) ? response.data : [];

const readCount = (response: any, fallback: number): number => {
  const value = response?.count ?? response?.totalCount;
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
};

/** Column header with a hover explanation. */
function HeaderHint({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={title} placement="top" describeChild>
      <span>{children}</span>
    </Tooltip>
  );
}

function CSITipaltiStagingList() {
  const toast = useToast();
  const userName = useCurrentUserName();
  // D1: only Admin may show a full tax ID.
  const canRevealTaxId = useMemo(() => isAdmin(), []);

  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [data, setData] = useState<CSITipaltiStaging[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const totalCountRef = useRef(0);
  const requestIdRef = useRef(0);
  const dataRef = useRef<CSITipaltiStaging[]>([]);
  dataRef.current = data;

  // Search and filters: the same state and query parameters as before.
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterInTipalti, setFilterInTipalti] = useState<TriState>(null);
  const [filterMatched, setFilterMatched] = useState<TriState>(null);
  const [filterPayable, setFilterPayable] = useState<TriState>(null);
  const [filterActive, setFilterActive] = useState<TriState>(true);
  const [filterGroupsMode, setFilterGroupsMode] = useState<GroupsMode>("all");
  const [filterMigratedBills, setFilterMigratedBills] =
    useState<TriState>(null);
  const [filterNonMigratedBills, setFilterNonMigratedBills] =
    useState<TriState>(null);
  const [filterCsiPaymentMethod, setFilterCsiPaymentMethod] =
    useState<string>("all");
  const [filterReviewedResults, setFilterReviewedResults] =
    useState<string>("all");

  // Selection (kept by id with the row, so it survives paging) and the bulk bar.
  const [selected, setSelected] = useState<Map<string, CSITipaltiStaging>>(
    () => new Map()
  );
  const [massMemo, setMassMemo] = useState("");
  const [massReviewedResults, setMassReviewedResults] = useState("");
  const [bulkProgress, setBulkProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  // Dialogs.
  const [payeeModalRowId, setPayeeModalRowId] = useState<string | null>(null);
  const [tinMatchModalTin, setTinMatchModalTin] = useState<string | null>(null);
  const [payeeDetailModalId, setPayeeDetailModalId] = useState<string | null>(
    null
  );
  const [billDetailModalRefCode, setBillDetailModalRefCode] = useState<
    string | null
  >(null);

  // Debounce the search box; a new search starts at the first page.
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchStagingData = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;
    setLoading(true);
    setError(null);

    const load = (pageSize: number) =>
      agent.CSITipalti.getAllAnalysis(
        page + 1,
        pageSize,
        filterActive,
        debouncedSearchTerm,
        "CsiCreatedDate",
        "desc",
        filterInTipalti,
        filterMatched,
        filterPayable,
        filterGroupsMode,
        filterMigratedBills,
        filterNonMigratedBills,
        filterCsiPaymentMethod,
        filterReviewedResults
      );

    try {
      // "All" asks for every matching row: the last known total, and once more with
      // the new total if it has grown since.
      const pageSize = effectivePageSize(rowsPerPage, totalCountRef.current);
      let response = await load(pageSize);
      if (rowsPerPage === ALL_ROWS) {
        const total = readCount(response, 0);
        if (total > pageSize && isCurrent()) response = await load(total);
      }
      if (!isCurrent()) return;

      const rows = readItems(response).map(toStagingRow);
      const total = readCount(response, rows.length);
      totalCountRef.current = total;
      setData(rows);
      setTotalCount(total);
      setHasLoaded(true);
      // Selected rows that were reloaded carry their latest values into a bulk update.
      setSelected((prev) => {
        if (prev.size === 0) return prev;
        let changed = false;
        const next = new Map(prev);
        rows.forEach((row) => {
          if (next.has(row.id)) {
            next.set(row.id, row);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    } catch {
      if (!isCurrent()) return;
      setData([]);
      setError("Check your connection and try again.");
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    filterActive,
    debouncedSearchTerm,
    filterInTipalti,
    filterMatched,
    filterPayable,
    filterGroupsMode,
    filterMigratedBills,
    filterNonMigratedBills,
    filterCsiPaymentMethod,
    filterReviewedResults,
  ]);

  useEffect(() => {
    fetchStagingData();
  }, [fetchStagingData]);

  // If the total shrank (e.g. rows marked inactive), stay on a page that exists.
  useEffect(() => {
    if (!hasLoaded || rowsPerPage === ALL_ROWS || page === 0) return;
    const lastPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [hasLoaded, page, rowsPerPage, totalCount]);

  /** Wraps a filter setter so a change also returns to the first page. */
  const onFilter =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setPage(0);
    };

  const resetFilters = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setFilterInTipalti(null);
    setFilterMatched(null);
    setFilterPayable(null);
    setFilterActive(true);
    setFilterGroupsMode("all");
    setFilterMigratedBills(null);
    setFilterNonMigratedBills(null);
    setFilterCsiPaymentMethod("all");
    setFilterReviewedResults("all");
    setSelected(new Map());
    setMassMemo("");
    setMassReviewedResults("");
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage: TablePaginationProps["onRowsPerPageChange"] = (
    event
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /* ------------------------------------------------------------ row handlers */

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelected = useCallback((row: CSITipaltiStaging) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });
  }, []);

  const handleRowSaved = useCallback((updated: CSITipaltiStaging) => {
    setData((rows) =>
      rows.map((row) => (row.id === updated.id ? updated : row))
    );
    setSelected((prev) => {
      if (!prev.has(updated.id)) return prev;
      const next = new Map(prev);
      next.set(updated.id, updated);
      return next;
    });
  }, []);

  const handleTinClick = useCallback((tin: string) => {
    setTinMatchModalTin(tin);
  }, []);

  const handleMainPayeeIdClick = useCallback((payeeId: string) => {
    setPayeeDetailModalId(payeeId);
  }, []);

  // Opens the payee's analysis record when it is on this page; otherwise its CSI Pay
  // record, so the link never silently does nothing.
  const handlePayeeIdClick = useCallback(
    (payeeId: string) => {
      const found = dataRef.current.find((item) => item.payeeId === payeeId);
      if (found) {
        setPayeeModalRowId(found.id);
      } else {
        setPayeeDetailModalId(payeeId);
        toast.info(
          "That payee isn't on this page, so its CSI Pay record is shown instead."
        );
      }
    },
    [toast]
  );

  const handleBillRefCodeClick = useCallback((billRefCode: string) => {
    setBillDetailModalRefCode(billRefCode);
  }, []);

  /* --------------------------------------------------------------- selection */

  const selectedOnPage = data.filter((row) => selected.has(row.id)).length;
  const allOnPageSelected = data.length > 0 && selectedOnPage === data.length;

  const handleSelectAllOnPage = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) data.forEach((row) => next.delete(row.id));
      else data.forEach((row) => next.set(row.id, row));
      return next;
    });
  };

  /* ------------------------------------------------------------- bulk update */

  const memoLength = massMemo.trim().length;
  const memoTooShort = memoLength > 0 && memoLength < MEMO_MIN_LENGTH;
  const bulkRunning = bulkProgress !== null;
  const canBulkUpdate =
    selected.size > 0 &&
    memoLength >= MEMO_MIN_LENGTH &&
    !!massReviewedResults &&
    !bulkRunning;

  // Same as before: one POST at a time, each with the full row plus Active, memo and
  // review result. Now it carries on past a failure and reports both counts.
  const handleMassUpdateActive = async (isActive: boolean) => {
    if (!canBulkUpdate) return;
    const rows = Array.from(selected.values());
    const stamp = { at: new Date().toISOString(), by: userName };
    const change = {
      memo: massMemo,
      reviewedResults: massReviewedResults,
      isActive,
    };
    const failed = new Map<string, CSITipaltiStaging>();
    let succeeded = 0;

    for (let i = 0; i < rows.length; i++) {
      setBulkProgress({ current: i + 1, total: rows.length });
      const row = rows[i];
      try {
        await agent.CSITipalti.updateCSITipalti(
          row.id,
          buildReviewPayload(row, change, stamp)
        );
        succeeded++;
      } catch {
        failed.set(row.id, row);
      }
    }
    setBulkProgress(null);

    const marked = `marked ${isActive ? "active" : "inactive"}`;
    if (failed.size === 0) {
      toast.success(`${plural(succeeded, "payee")} ${marked}`);
      setSelected(new Map());
      setMassMemo("");
      setMassReviewedResults("");
    } else {
      toast.error(
        succeeded > 0
          ? `${plural(
              succeeded,
              "payee"
            )} ${marked}, ${failed.size.toLocaleString()} failed. The failed ones are still selected.`
          : `Couldn't update ${plural(
              failed.size,
              "payee"
            )}. They are still selected; try again.`
      );
      setSelected(failed);
    }
    await fetchStagingData();
  };

  /* ------------------------------------------------------------------ render */

  const triFilters: {
    label: string;
    value: TriState;
    set: (value: TriState) => void;
  }[] = [
    { label: "In Tipalti", value: filterInTipalti, set: setFilterInTipalti },
    { label: "Matched", value: filterMatched, set: setFilterMatched },
    { label: "Payable", value: filterPayable, set: setFilterPayable },
    {
      label: "Migrated bills",
      value: filterMigratedBills,
      set: setFilterMigratedBills,
    },
    {
      label: "Non-migrated bills",
      value: filterNonMigratedBills,
      set: setFilterNonMigratedBills,
    },
    { label: "Active", value: filterActive, set: setFilterActive },
  ];

  const payeeModalItem = payeeModalRowId
    ? data.find((row) => row.id === payeeModalRowId) ?? null
    : null;

  const showInitialLoading = loading && !hasLoaded;

  return (
    <Box>
      <PageHeader
        title="CSI-Tipalti Analysis"
        helmetTitle="CSI-Tipalti Analysis"
        subtitle="Compare each CSI Pay payee with its Tipalti record, and record what was decided."
        breadcrumbs={[
          { label: "Reconciliation" },
          { label: "CSI-Tipalti Analysis" },
        ]}
      />

      <Card>
        {/* Filters */}
        <Box
          component="section"
          aria-label="Filters"
          sx={{ px: 4, pt: 4, pb: 3.5, display: "grid", gap: 3 }}
        >
          <Box
            role="group"
            aria-label="Yes / no filters"
            sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}
          >
            {triFilters.map((filter) => (
              <TriFilterChip
                key={filter.label}
                label={filter.label}
                value={filter.value}
                onChange={onFilter(filter.set)}
              />
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 3,
            }}
          >
            <TextField
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, payee ID or email"
              inputProps={{ "aria-label": "Search by name, payee ID or email" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: "1 1 260px", minWidth: 220, maxWidth: 380 }}
            />
            <TextField
              select
              size="small"
              label="Group"
              value={filterGroupsMode}
              onChange={(e) =>
                onFilter(setFilterGroupsMode)(e.target.value as GroupsMode)
              }
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All groups</MenuItem>
              <MenuItem value="single">Single groups</MenuItem>
              <MenuItem value="multiple">Multiple groups</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Payment method"
              value={filterCsiPaymentMethod}
              onChange={(e) =>
                onFilter(setFilterCsiPaymentMethod)(e.target.value)
              }
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All methods</MenuItem>
              <MenuItem value="ach">ACH</MenuItem>
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Review"
              value={filterReviewedResults}
              onChange={(e) =>
                onFilter(setFilterReviewedResults)(e.target.value)
              }
              sx={{ minWidth: 220 }}
            >
              {REVIEW_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button onClick={resetFilters}>Reset</Button>
          </Box>
        </Box>

        {/* Bulk Active / Inactive with a memo */}
        {selected.size > 0 && (
          <Box
            component="section"
            aria-label="Update selected payees"
            sx={(theme) => ({
              backgroundColor: theme.palette.brandTint,
              borderTop: `1px solid ${theme.palette.divider}`,
              px: 4,
              py: 3,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              gap: 3,
            })}
          >
            <Typography
              sx={{
                color: "primary.main",
                fontWeight: 500,
                pt: 2,
                minWidth: 80,
              }}
            >
              {selected.size.toLocaleString()} selected
            </Typography>
            <TextField
              size="small"
              label="Memo for this update"
              value={massMemo}
              onChange={(e) => setMassMemo(e.target.value)}
              multiline
              maxRows={2}
              required
              disabled={bulkRunning}
              error={memoTooShort}
              helperText={
                memoTooShort
                  ? `At least ${MEMO_MIN_LENGTH} characters (${
                      MEMO_MIN_LENGTH - memoLength
                    } more).`
                  : `At least ${MEMO_MIN_LENGTH} characters. Saved on each record.`
              }
              sx={{
                flex: "1 1 280px",
                minWidth: 240,
                "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
              }}
            />
            <TextField
              select
              size="small"
              label="Review result"
              value={massReviewedResults}
              onChange={(e) => setMassReviewedResults(e.target.value)}
              required
              disabled={bulkRunning}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              helperText="Required"
              sx={{
                minWidth: 220,
                "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
              }}
            >
              <MenuItem value="" disabled>
                Choose a result
              </MenuItem>
              {REVIEW_RESULTS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              onClick={() => handleMassUpdateActive(false)}
              disabled={!canBulkUpdate}
              sx={{ bgcolor: "background.paper" }}
            >
              Mark inactive
            </Button>
            <Button
              variant="contained"
              onClick={() => handleMassUpdateActive(true)}
              disabled={!canBulkUpdate}
            >
              Mark active
            </Button>
            <Button
              onClick={() => setSelected(new Map())}
              disabled={bulkRunning}
            >
              Clear selection
            </Button>
            {bulkProgress && (
              <Box sx={{ flexBasis: "100%" }} role="status">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Updating {bulkProgress.current.toLocaleString()} of{" "}
                  {bulkProgress.total.toLocaleString()}…
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={
                    ((bulkProgress.current - 1) / bulkProgress.total) * 100
                  }
                  aria-label="Bulk update progress"
                />
              </Box>
            )}
          </Box>
        )}

        {error && (
          <Box sx={{ px: 4, pb: 3, pt: selected.size > 0 ? 3 : 0 }}>
            <Callout
              tone="err"
              role="alert"
              title="Couldn't load the analysis"
              action={
                <Button color="inherit" size="small" onClick={fetchStagingData}>
                  Retry
                </Button>
              }
            >
              {error}
            </Callout>
          </Box>
        )}

        {/* Thin progress bar while reloading, so the table and search stay in place. */}
        <Box sx={{ height: 4 }}>
          {loading && hasLoaded && (
            <LinearProgress aria-label="Loading the analysis" />
          )}
        </Box>

        <TableContainer>
          <Table
            size="small"
            aria-label="CSI-Tipalti Analysis"
            aria-busy={loading}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allOnPageSelected}
                    indeterminate={selectedOnPage > 0 && !allOnPageSelected}
                    onChange={handleSelectAllOnPage}
                    disabled={data.length === 0 || bulkRunning}
                    inputProps={{
                      "aria-label": "Select all rows on this page",
                    }}
                  />
                </TableCell>
                <TableCell>Payee</TableCell>
                <TableCell>
                  <HeaderHint title="Whether this payee exists in Tipalti">
                    In Tipalti
                  </HeaderHint>
                </TableCell>
                <TableCell>
                  <HeaderHint title="Whether this CSI Pay payee was matched with a Tipalti payee record">
                    Matched
                  </HeaderHint>
                </TableCell>
                <TableCell>
                  <HeaderHint title="Whether this payee can receive payments">
                    Payable
                  </HeaderHint>
                </TableCell>
                <TableCell>
                  <HeaderHint title="Whether this record is still active">
                    Active
                  </HeaderHint>
                </TableCell>
                <TableCell>
                  <HeaderHint title="The CSI Pay payment method">
                    Pays by
                  </HeaderHint>
                </TableCell>
                <TableCell>Review</TableCell>
                <TableCell>
                  <Box component="span" sx={visuallyHidden}>
                    Details
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {showInitialLoading && (
                <TableRow>
                  <TableCell colSpan={STAGING_COLUMN_COUNT} sx={{ py: 10 }}>
                    <Box
                      role="status"
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 2,
                        color: "text.secondary",
                      }}
                    >
                      <CircularProgress size={20} aria-hidden />
                      Loading the analysis…
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!showInitialLoading && !error && data.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={STAGING_COLUMN_COUNT}
                    align="center"
                    sx={{ py: 10, color: "text.secondary" }}
                  >
                    {loading
                      ? "Loading the analysis…"
                      : "No payees match these filters."}
                    {!loading && (
                      <Box sx={{ mt: 2 }}>
                        <Button size="small" onClick={resetFilters}>
                          Reset filters
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              )}
              {!showInitialLoading &&
                data.map((row, index) => (
                  <StagingRow
                    key={row.id || `row-${index}`}
                    row={row}
                    selected={selected.has(row.id)}
                    expanded={expandedIds.has(row.id)}
                    onToggleSelected={toggleSelected}
                    onToggleExpanded={toggleExpanded}
                    onSaved={handleRowSaved}
                    canRevealTaxId={canRevealTaxId}
                    selectionDisabled={bulkRunning}
                    onOpenPayeeDetail={handleMainPayeeIdClick}
                    onOpenTaxIdMatches={handleTinClick}
                    onOpenMatchedPayee={handlePayeeIdClick}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
          labelDisplayedRows={({ from, to, count }) =>
            `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`
          }
        />
      </Card>

      <PayeeModal
        item={payeeModalItem}
        open={!!payeeModalItem}
        onClose={() => setPayeeModalRowId(null)}
        onSaved={handleRowSaved}
        canRevealTaxId={canRevealTaxId}
        onOpenPayeeDetail={handleMainPayeeIdClick}
        onOpenMatchedPayee={handlePayeeIdClick}
      />
      <TinMatchModal
        tin={tinMatchModalTin || ""}
        items={data}
        open={!!tinMatchModalTin}
        onClose={() => setTinMatchModalTin(null)}
        onPayeeIdClick={handlePayeeIdClick}
        canRevealTaxId={canRevealTaxId}
      />
      <PayeeDetailModal
        payeeId={payeeDetailModalId}
        open={!!payeeDetailModalId}
        onClose={() => setPayeeDetailModalId(null)}
        onBillRefCodeClick={handleBillRefCodeClick}
      />
      <BillDetailModal
        billRefCode={billDetailModalRefCode}
        open={!!billDetailModalRefCode}
        onClose={() => setBillDetailModalRefCode(null)}
      />
    </Box>
  );
}

export default CSITipaltiStagingList;
